import { useCallback, useEffect, useMemo, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { ColumnDef, ColumnPriority } from "./types";

const STORAGE_PREFIX = "ukar.dataTable.columns.";

/**
 * Which priorities survive at the current breakpoint. See ColumnPriority.
 */
function autoVisible(priority: ColumnPriority, isMdUp: boolean, isLgUp: boolean) {
  if (priority === "essential") return true;
  if (priority === "high") return isMdUp;
  return isLgUp;
}

function readStored(tableId: string): Record<string, boolean> | null {
  // Storage is a per-viewer convenience only. It is absent in private windows,
  // can throw when site data is blocked, and must never be load-bearing — the
  // breakpoint default below is always a correct fallback.
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + tableId);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : null;
  } catch {
    return null;
  }
}

function writeStored(tableId: string, value: Record<string, boolean>) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + tableId, JSON.stringify(value));
  } catch {
    /* ignore — see readStored */
  }
}

/**
 * Resolves which columns are visible, combining the responsive default with the
 * user's explicit picks.
 *
 * Precedence: an explicit user choice always wins, including re-enabling a
 * column the breakpoint would have hidden — that is the whole point of the
 * picker on a phone. Columns the user has never touched follow the breakpoint,
 * so widening the window reveals more without clearing their overrides.
 *
 * `essential` columns are structurally unhideable: they are filtered out of the
 * picker and forced true here, so no stored value can strand a user with a table
 * they cannot identify rows in or act on.
 */
export function useColumnVisibility<T>(tableId: string, columns: ColumnDef<T>[]) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const [overrides, setOverrides] = useState<Record<string, boolean>>(
    () => readStored(tableId) ?? {}
  );

  useEffect(() => {
    setOverrides(readStored(tableId) ?? {});
  }, [tableId]);

  const visibleColumns = useMemo(
    () =>
      columns.filter((c) => {
        if (c.priority === "essential") return true;
        const override = overrides[c.id];
        return override === undefined
          ? autoVisible(c.priority, isMdUp, isLgUp)
          : override;
      }),
    [columns, overrides, isMdUp, isLgUp]
  );

  const isVisible = useCallback(
    (id: string) => visibleColumns.some((c) => c.id === id),
    [visibleColumns]
  );

  const toggleColumn = useCallback(
    (id: string) => {
      setOverrides((prev) => {
        const column = columns.find((c) => c.id === id);
        if (!column || column.priority === "essential") return prev;
        const current =
          prev[id] === undefined
            ? autoVisible(column.priority, isMdUp, isLgUp)
            : prev[id];
        const next = { ...prev, [id]: !current };
        writeStored(tableId, next);
        return next;
      });
    },
    [columns, isMdUp, isLgUp, tableId]
  );

  const resetColumns = useCallback(() => {
    setOverrides({});
    writeStored(tableId, {});
  }, [tableId]);

  return { visibleColumns, isVisible, toggleColumn, resetColumns };
}
